/**
 * Logica condivisa per il gate abbonamento (App.js) e la pagina Fatturazione.
 *
 * Il backend (getDaysLeft / subscription/status / invoices) espone:
 *  - paymentMethodType: 'card' | 'sepa_debit' | ...
 *  - paymentStatus (getDaysLeft / subscription/status) e paymentIntentStatus (invoices):
 *    stati PaymentIntent Stripe as-is — vedi PAYMENT_INTENT_STATUS
 *  - status (invoices): stati Invoice Stripe — vedi INVOICE_STATUS
 *  - frequency (su invoices): { interval, intervalCount, label }
 *  - subscriptionFrequency (su /customer)
 *
 * Con RID/SEPA l'addebito resta "processing" per ~7–15 giorni dopo la scadenza:
 * in quel periodo NON blocchiamo la piattaforma e NON mostriamo "Paga ora".
 *
 * Blocco hard: solo dal giorno successivo alla `scadenza` (es. scade il 9 → lock dal 10),
 * così Stripe può rinnovare in giornata. I numeri banner usano ancora `giorniRimanenti`.
 *
 * Nota sicurezza: payment_intent e charge devono restare ID stringa (no expand),
 * altrimenti il client_secret del PI finisce nel browser.
 */

/** Giorni di tolleranza oltre la scadenza per un RID ancora in elaborazione. */
export const SEPA_GRACE_DAYS = 15;

export const PAYMENT_METHOD = {
  CARD: 'card',
  SEPA: 'sepa_debit',
};

/**
 * Stati Invoice Stripe (campo `status` se presente; altrimenti si usa `paid`).
 * @see https://docs.stripe.com/invoicing/overview#invoice-statuses
 */
export const INVOICE_STATUS = {
  DRAFT: 'draft',
  OPEN: 'open',
  PAID: 'paid',
  VOID: 'void',
  UNCOLLECTIBLE: 'uncollectible',
};

/**
 * Vocabolario allineato agli status Stripe PaymentIntent (confermato BE).
 *
 * | Valore                    | Uso tipico FE                                      |
 * |---------------------------|----------------------------------------------------|
 * | requires_payment_method   | pagamento fallito / metodo mancante → Paga ora     |
 * | requires_confirmation     | conferma cliente → Paga ora                        |
 * | requires_action           | 3DS / autenticazione → Paga ora                    |
 * | processing                | pagamento in sospeso (SEPA) → Niente Paga ora      |
 * | requires_capture          | capture manuale → Paga ora                         |
 * | canceled                  | PI annullato → Paga ora / aggiorna metodo          |
 * | succeeded                 | ok                                                 |
 * | null                      | fattura open senza PI → Paga ora se pagabile       |
 */
export const PAYMENT_INTENT_STATUS = {
  REQUIRES_PAYMENT_METHOD: 'requires_payment_method',
  REQUIRES_CONFIRMATION: 'requires_confirmation',
  REQUIRES_ACTION: 'requires_action',
  PROCESSING: 'processing',
  REQUIRES_CAPTURE: 'requires_capture',
  CANCELED: 'canceled',
  SUCCEEDED: 'succeeded',
};

const toInvoiceList = (invoices) =>
  Array.isArray(invoices) ? invoices : invoices?.invoices || [];

const invoiceTimestamp = (invoice) => Number(invoice?.date ?? invoice?.created) || 0;

/** Status da getDaysLeft/subscription o da fattura (paymentIntentStatus). */
export const getPaymentStatus = (products, fallback) =>
  products?.paymentStatus || fallback?.paymentStatus || null;

export const getInvoicePaymentStatus = (invoice) =>
  invoice?.paymentIntentStatus || null;

export const isPaymentProcessing = (status) =>
  status === PAYMENT_INTENT_STATUS.PROCESSING;

/** Alias semantico: pagamento già avviato in attesa di esito (tipicamente RID). */
export const isPaymentPending = (status) => isPaymentProcessing(status);

/** Fallito / metodo mancante / PI annullato → l'utente deve riprovare o aggiornare il metodo. */
export const isPaymentFailed = (status) =>
  status === PAYMENT_INTENT_STATUS.REQUIRES_PAYMENT_METHOD ||
  status === PAYMENT_INTENT_STATUS.CANCELED;

/** 3DS o conferma cliente ancora necessaria. */
export const isPaymentActionRequired = (status) =>
  status === PAYMENT_INTENT_STATUS.REQUIRES_ACTION ||
  status === PAYMENT_INTENT_STATUS.REQUIRES_CONFIRMATION;

/**
 * PI per cui ha senso aprire la hosted invoice / chiedere un pagamento all'utente.
 * Esclude processing (in sospeso) e succeeded.
 */
export const paymentIntentAllowsPayNow = (status) => {
  if (status == null) return true; // open invoice senza PI ancora creato
  if (isPaymentPending(status)) return false;
  if (status === PAYMENT_INTENT_STATUS.SUCCEEDED) return false;
  return (
    status === PAYMENT_INTENT_STATUS.REQUIRES_PAYMENT_METHOD ||
    status === PAYMENT_INTENT_STATUS.REQUIRES_CONFIRMATION ||
    status === PAYMENT_INTENT_STATUS.REQUIRES_ACTION ||
    status === PAYMENT_INTENT_STATUS.REQUIRES_CAPTURE ||
    status === PAYMENT_INTENT_STATUS.CANCELED
  );
};

/**
 * Fattura non liquidata (candidata a recovery), escluse draft/void/uncollectible/paid.
 */
export const isInvoiceUnsettled = (invoice) => {
  if (!invoice) return false;
  if (invoice.paid === true) return false;

  const status = invoice.status;
  if (
    status === INVOICE_STATUS.PAID ||
    status === INVOICE_STATUS.VOID ||
    status === INVOICE_STATUS.UNCOLLECTIBLE ||
    status === INVOICE_STATUS.DRAFT
  ) {
    return false;
  }

  // Stripe: open, oppure payload legacy solo con paid:false
  return status == null || status === INVOICE_STATUS.OPEN || invoice.paid === false;
};

/**
 * Unpaid "storica": esiste almeno una fattura pagata più recente.
 * Non deve guidare recovery / Paga ora sull'abbonamento attivo.
 */
export const isStaleUnpaidInvoice = (invoice, invoices) => {
  if (!invoice || invoice.paid) return false;
  const unpaidTs = invoiceTimestamp(invoice);
  return toInvoiceList(invoices).some(
    (inv) => inv?.paid && invoiceTimestamp(inv) > unpaidTs,
  );
};

/**
 * Fattura aperta rilevante per l'abbonamento corrente (non storica).
 */
export const getRelevantOpenInvoice = (invoices) => {
  const list = toInvoiceList(invoices);
  return (
    list.find((inv) => isInvoiceUnsettled(inv) && !isStaleUnpaidInvoice(inv, list)) || null
  );
};

/**
 * True se la fattura richiede "Paga ora":
 * - unsettled e non stale
 * - ha hosted_invoice_url (pagabile su Stripe)
 * - nessun pagamento in sospeso (PI !== processing)
 * - PI in stato che richiede azione cliente, oppure assente
 */
export const invoiceRequiresPayNow = (invoice, invoices) => {
  if (!invoice) return false;
  if (!isInvoiceUnsettled(invoice)) return false;
  if (invoices && isStaleUnpaidInvoice(invoice, invoices)) return false;
  if (!invoice.hosted_invoice_url) return false;
  return paymentIntentAllowsPayNow(getInvoicePaymentStatus(invoice));
};

/**
 * Prima fattura su cui ha senso mostrare "Paga ora".
 */
export const getPayableInvoice = (invoices) => {
  const list = toInvoiceList(invoices);
  return list.find((inv) => invoiceRequiresPayNow(inv, list)) || null;
};

/**
 * Fattura con pagamento in sospeso (niente Paga ora → banner attesa / refresh).
 */
export const invoiceHasPendingPayment = (invoice, invoices) => {
  if (!invoice) return false;
  if (!isInvoiceUnsettled(invoice)) return false;
  if (invoices && isStaleUnpaidInvoice(invoice, invoices)) return false;
  return isPaymentPending(getInvoicePaymentStatus(invoice));
};

export const getDaysLeft = (products) => {
  const n = Number(products?.giorniRimanenti);
  return Number.isFinite(n) ? n : null;
};

const startOfLocalDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Interpreta `scadenza` (YYYY-MM-DD o ISO) come mezzanotte locale. */
export const parseScadenzaDay = (scadenza) => {
  if (scadenza == null || scadenza === '') return null;
  const raw = String(scadenza);
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfLocalDay(parsed);
};

/**
 * True se è già trascorso almeno un giorno intero dopo la scadenza
 * (scade il 9 → blocco dal 10). Non altera i numeri banner (`giorniRimanenti`).
 */
export const isPastFullDayAfterExpiry = (products) => {
  const expiry = parseScadenzaDay(products?.scadenza);
  if (expiry) {
    return startOfLocalDay(new Date()).getTime() > expiry.getTime();
  }
  // Fallback senza scadenza: solo oltre lo zero (giorno di scadenza già passato)
  const days = getDaysLeft(products);
  return days != null && days < 0;
};

/** True se `giorniRimanenti <= 0` (solo copy/banner; il lock usa isPastFullDayAfterExpiry). */
export const isExpired = (products) => {
  const days = getDaysLeft(products);
  return days != null && days <= 0;
};

export const getPaymentMethodType = (products, fallback) =>
  products?.paymentMethodType || fallback?.paymentMethodType || null;

export const isSepa = (products, fallback) =>
  getPaymentMethodType(products, fallback) === PAYMENT_METHOD.SEPA;

export const isSepaProcessing = (products, fallback) =>
  isSepa(products, fallback) && isPaymentProcessing(getPaymentStatus(products, fallback));

/** Il RID è in elaborazione e siamo ancora dentro la finestra di tolleranza. */
export const isInSepaGrace = (products, fallback) => {
  if (!isSepaProcessing(products, fallback)) return false;
  const days = getDaysLeft(products);
  return days != null && days > -SEPA_GRACE_DAYS;
};

/**
 * True se la piattaforma va bloccata (solo pagina Fatturazione).
 * Blocco dal giorno successivo alla scadenza (stripe può rinnovare in giornata),
 * salvo RID in elaborazione entro la grace.
 */
export const isSubscriptionLocked = (products, fallback) =>
  isPastFullDayAfterExpiry(products) && !isInSepaGrace(products, fallback);

/**
 * Data prevista di addebito SEPA (YYYY-MM-DD), se il BE la espone come campo derivato.
 * Con charge come ID stringa (payload snello) resta null — ok, il copy non dipende da essa.
 */
export const getExpectedDebitDate = (invoice) =>
  invoice?.expectedDebitDate ||
  invoice?.charge?.payment_method_details?.sepa_debit?.expected_debit_date ||
  null;
